<template>
  <div class="page">
    <div class="login-form w-75 mx-auto mt-5">
      <!-- 開発環境以外ではLoadingViewを表示 -->
      <loading v-if="useLoadingView" :msg="status" />

      <b-card v-else no-body class="overflow-hidden">
        <b-card-header>
          <div>{{ status }}</div>
        </b-card-header>
        <b-row no-gutters>
          <b-col
            xs="2"
            sm="4"
            md="3"
            class="mt-3 mt-sm-0 d-flex align-items-center user-select-none"
          >
            <b-img center src="/img/app_icon_forum.svg" width="135px" height="135px" />
          </b-col>

          <b-col xs="10" sm="8" md="9">
            <b-card-body class="pl-sm-1 py-xs-0 mt-xs-3">
              <b-row v-if="stage == 'PRD'" class="mx-sm-1">
                <b-button
                  variant="outline-info"
                  @click="signIn('direct')"
                  class="w-100 login-button"
                  :disabled="loginContinue"
                >
                  ログイン<span style="position: absolute; right: 4px">(direct {{ stage }})</span>
                </b-button>
              </b-row>
              <b-row v-if="stage == 'STG'" class="mx-sm-1">
                <b-button
                  variant="outline-info"
                  @click="signIn('direct-staging')"
                  class="w-100 login-button"
                  :disabled="loginContinue"
                >
                  ログイン<span style="position: absolute; right: 4px">(direct {{ stage }})</span>
                </b-button>
              </b-row>
              <b-row v-if="stage == 'DEV'" class="mx-sm-1">
                <b-button
                  variant="outline-info"
                  @click="signIn('direct-dev')"
                  class="w-100 login-button"
                  :disabled="loginContinue"
                >
                  ログイン<span style="position: absolute; right: 4px">(direct {{ stage }})</span>
                </b-button>
              </b-row>

              <b-row class="mx-sm-1">
                <b-button
                  variant="outline-info"
                  @click="signIn('ios')"
                  class="w-100 login-button"
                  :disabled="loginContinue"
                >
                  ログイン<span style="position: absolute; right: 4px"
                    >(iOS(direct {{ stage }}))</span
                  >
                </b-button>
              </b-row>

              <b-row class="mx-sm-1">
                <b-button
                  variant="outline-info"
                  @click="signIn('android')"
                  class="w-100 login-button"
                  :disabled="loginContinue"
                >
                  ログイン<span style="position: absolute; right: 4px"
                    >(Android(direct {{ stage }}))</span
                  >
                </b-button>
              </b-row>
            </b-card-body>
          </b-col>
        </b-row>
        <b-card-footer>
          <b-row class="mx-5">
            <b-button variant="outline-info" @click="signOut" class="w-100">
              <template v-if="loginContinue">キャンセル</template>
              <template v-else>ログアウト</template>
            </b-button>
          </b-row>
        </b-card-footer>
      </b-card>
    </div>
  </div>
</template>
<style scoped>
.login-button {
  position: relative;
  text-align: left;
}
.login-form {
  margin: 20;
}
.btn.disabled {
  background-color: lightgray;
  opacity: 0.4;
}

.code-value {
  flex: 1 1 0;
  border: 1px solid #bfbfbf;
  background-color: #f5f5f5;
  text-align: left;
}

/* md */
@media (min-width: 768px) {
  .login-button {
    text-align: center;
  }
}
</style>
<script lang="ts">
import AppAuth from "../aws-config";
import { useAppStore } from "@/store";
import { PRODUCT_STAGE } from "../aws-config";
import type { PROVIDER } from "../aws-config";
import Loading from "../components/loading/Loading.vue";
import { isMobile, osType, OS_TYPE } from "../mobile";
import mobileLoginWorkflow from "./mobile-login-workflow";

import { computed, defineComponent, getCurrentInstance, nextTick, onMounted, ref } from "vue";
import type Vue from "vue";
import { LoadingManager } from "../components/loading/loading-manager";
import sentry from "@/sentry";

enum LoadingMngKey {
  LOGIN = "login",
  LOGIN_MOBILE_REDIRECT = "loginMobile",
}

export default defineComponent({
  name: "login",
  components: { Loading },
  setup() {
    // $route は vue-router が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const status = ref<string>("");
    const message_text = ref<string>("");
    const loginContinue = ref<boolean>(false);
    const stage = ref(PRODUCT_STAGE);
    const useLoadingView = ref<boolean>(true); // true: ログイン画面を出さず、ロード中画面にする
    const count = ref<number>(0); // リトライのカウント数

    const loginRestrictDesc = computed((): string => {
      return (vm?.$route.query?.desc || "") as string;
    });

    const sendLoadingManager = (category: LoadingMngKey, msg: string): void => {
      if (!useLoadingView.value) LoadingManager.start(category, msg);
    };

    const stopLoadingManager = (category: LoadingMngKey): void => {
      if (!useLoadingView.value) LoadingManager.stop(category);
    };

    const signIn = async (provider: PROVIDER | OS_TYPE): Promise<void> => {
      try {
        switch (provider) {
          case "ios":
            await mobileSignInWorkflow("direct-mobile", "ios");
            break;
          case "android":
            await mobileSignInWorkflow("direct-mobile", "and");
            break;
          default:
            await AppAuth.signIn(provider as PROVIDER);
            break;
        }
        status.value = "ログイン中...";
      } catch (err) {
        sentry.sendSentryError(err);
        return;
      }
    };

    // モバイル向けサインインワークフロー
    const mobileSignInWorkflow = async (
      provider: PROVIDER,
      option: "ios" | "and"
    ): Promise<void> => {
      // dxflow:2429 モバイルログイン処理のタイミングを、画面ロード後にずらす(iOSで画面ロード中にページ読み込みが中止されたというエラーが出るため）
      void nextTick(() => {
        try {
          mobileLoginWorkflow(provider, option, (msg: string) => {
            sendLoadingManager(LoadingMngKey.LOGIN_MOBILE_REDIRECT, msg);
            status.value = msg;
          });
        } catch (error) {
          sentry.sendSentryError(error);
        } finally {
          stopLoadingManager(LoadingMngKey.LOGIN_MOBILE_REDIRECT);
        }
      });
    };

    const signOut = async (): Promise<void> => {
      const url = await AppAuth.signOut();
      if (url) {
        window.location.href = url;
      }
    };

    // created 相当
    loginContinue.value = (vm?.$route.path.indexOf("login-continue") ?? -1) != -1;

    onMounted(async (): Promise<void> => {
      try {
        if (loginContinue.value) {
          status.value = "ログイン中...";
          sendLoadingManager(LoadingMngKey.LOGIN, status.value);
          let provider: PROVIDER;
          switch (vm?.$route.path) {
            case "/login-continue":
              provider = "direct";
              break;
            case "/login-continue-stg":
              provider = "direct-staging";
              break;
            case "/login-continue-dev":
              provider = "direct-dev";
              break;
            default:
              provider = "direct";
              break;
          }
          try {
            await AppAuth.signIn(provider);
          } finally {
            stopLoadingManager(LoadingMngKey.LOGIN);
          }
        } else {
          const msg = "ログイン中...";
          sendLoadingManager(LoadingMngKey.LOGIN, msg);

          // mobile判定を$routeからすると失敗するパターンがあるため vuex 経由
          // ＊認可callback後など
          const device = useAppStore().device;
          if (isMobile(device)) {
            // スマホは別処理
            const type = osType(device);
            await signIn(type);
          } else {
            // Web版
            let provider: PROVIDER | "" = "";
            switch (stage.value) {
              case "PRD":
                provider = "direct";
                break;
              case "STG":
                provider = "direct-staging";
                break;
              case "DEV":
                provider = "direct-dev";
                break;
            }
            if (provider) {
              await AppAuth.signIn(provider);
              status.value = msg;
            }
          }
        }
      } finally {
        // サインインが例外で抜けた場合もローディング表示を必ず解除する
        count.value = 0;
        stopLoadingManager(LoadingMngKey.LOGIN);
      }
    });

    return {
      status,
      message_text,
      loginContinue,
      stage,
      useLoadingView,
      count,
      loginRestrictDesc,
      signIn,
      mobileSignInWorkflow,
      signOut,
    };
  },
});
</script>
